import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeHome } from "@navigation/typedRoutes";
import DemographicSurveyForm from "@view/demographic/DemographicSurveyForm";
import { usePaymentClient } from "@contexts/PaymentClient.context";
import { useLoadingController } from "@src/contexts/Loading.context";
import { makePromoCode } from "@ps-bridge/models/brands/promoCode";
import { ok } from "@ps-bridge/ok";
import { mkNavigate, mkTaskRunner } from "@ps-bridge/capabilities";
import { ioEffect } from "@utils/effects/branded";
import type { DemographicSurveyKernel } from "@ps-bridge/contracts/demographicSurvey";
import { buildDemographicSurveyViewProps } from "@ps-bridge/demographicSurvey.program";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import type { TaskTs } from "@ps-bridge/interop";
import type { DemographicInformation, PromoCode } from "@ps-bridge/models/core/dataContracts";
import type { Ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import { getParamOption } from "@utils/searchParams";

const DemographicSurveyPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentClient = usePaymentClient();
  const loading = useLoadingController();

  const promoCodeValue = useMemo(
    () => getParamOption(searchParams, "promo_code"),
    [searchParams],
  );
  const brandedPromoCode = useMemo(
    () => makePromoCode(Option.fold(promoCodeValue, () => "", (value) => value)),
    [promoCodeValue],
  );

  const runTaskWithError = useRunTaskWithError();
  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );
  const taskRunner = useMemo(
    () => mkTaskRunner(runTaskWithErrorIo),
    [runTaskWithErrorIo],
  );

  const paymentClientKernel = useMemo(
    () => ({
      ...paymentClient,
      submitDemographicInformation: (promoCode: PromoCode) => (demographicInfo: DemographicInformation) =>
        paymentClient.submitDemographicInformation(promoCode, demographicInfo),
    }),
    [paymentClient],
  );

  const kernel: DemographicSurveyKernel = useMemo(
    () => ({
      loading,
      paymentClient: paymentClientKernel,
      taskRunner,
      navigate: mkNavigate((route) =>
        ioEffect(() => {
          navigate(renderRoute(route));
          return ok;
        }),
      ),
    }),
    [loading, navigate, paymentClientKernel, taskRunner],
  );

  const viewProps = useMemo(
    () =>
      buildDemographicSurveyViewProps({
        kernel,
        promoCode: brandedPromoCode,
        homeRoute: routeHome,
      }),
    [brandedPromoCode, kernel],
  );

  return <DemographicSurveyForm {...viewProps} />;
};

export default DemographicSurveyPage;
